import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import Icon from '../../../components/AppIcon';

const SecurityTab = () => {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);

  const [activeSessions] = useState([
    {
      id: 1,
      device: 'MacBook Pro',
      browser: 'Chrome 118.0',
      location: 'New York, NY',
      ipAddress: '192.168.1.100',
      lastActive: '2 minutes ago',
      isCurrent: true
    },
    {
      id: 2,
      device: 'iPhone 15 Pro',
      browser: 'Safari Mobile',
      location: 'New York, NY',
      ipAddress: '192.168.1.101',
      lastActive: '1 hour ago',
      isCurrent: false
    },
    {
      id: 3,
      device: 'iPad Air',
      browser: 'Safari',
      location: 'Brooklyn, NY',
      ipAddress: '10.0.0.50',
      lastActive: '3 days ago',
      isCurrent: false
    }
  ]);

  const [loginHistory] = useState([
    {
      id: 1,
      timestamp: '2025-10-22 14:30:00',
      device: 'MacBook Pro',
      browser: 'Chrome 118.0',
      location: 'New York, NY',
      ipAddress: '192.168.1.100',
      status: 'success'
    },
    {
      id: 2,
      timestamp: '2025-10-22 09:15:00',
      device: 'iPhone 15 Pro',
      browser: 'Safari Mobile',
      location: 'New York, NY',
      ipAddress: '192.168.1.101',
      status: 'success'
    },
    {
      id: 3,
      timestamp: '2025-10-21 18:45:00',
      device: 'Unknown Device',
      browser: 'Chrome 117.0',
      location: 'Los Angeles, CA',
      ipAddress: '203.0.113.45',
      status: 'blocked'
    }
  ]);

  const handleEnableTwoFactor = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    setTwoFactorEnabled(true);
    setShowTwoFactorSetup(false);
    setLoading(false);
  };

  const handleDisableTwoFactor = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setTwoFactorEnabled(false);
    setLoading(false);
  };

  const handleRevokeSession = (sessionId) => {
    // Handle session revocation
    console.log('Revoking session:', sessionId);
  };

  const getDeviceIcon = (device) => {
    if (device?.includes('iPhone') || device?.includes('Android')) return 'Smartphone';
    if (device?.includes('iPad') || device?.includes('Tablet')) return 'Tablet';
    if (device?.includes('Mac') || device?.includes('Windows')) return 'Laptop';
    return 'Monitor';
  };

  return (
    <div className="space-y-8">
      {/* Two-Factor Authentication */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Two-Factor Authentication</h3>
            <p className="text-sm text-muted-foreground">
              Add an extra layer of security to your account
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              twoFactorEnabled 
                ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
            }`}>
              <Icon 
                name={twoFactorEnabled ? 'Shield' : 'ShieldAlert'} 
                size={12} 
                className="mr-1" 
              />
              {twoFactorEnabled ? 'Enabled' : 'Disabled'}
            </span>
            {!twoFactorEnabled ? (
              <Button
                variant="default"
                iconName="Shield"
                iconPosition="left"
                onClick={() => setShowTwoFactorSetup(true)}
              >
                Enable 2FA
              </Button>
            ) : (
              <Button
                variant="outline"
                loading={loading}
                onClick={handleDisableTwoFactor}
              >
                Disable 2FA
              </Button>
            )}
          </div>
        </div>

        {twoFactorEnabled && (
          <div className="bg-success/5 border border-success/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground mb-1">Two-Factor Authentication is Active</h4>
                <p className="text-sm text-muted-foreground">
                  Your account is protected with authenticator app-based 2FA. You'll need to enter a code from your authenticator app when signing in.
                </p>
                <div className="mt-3 flex space-x-2">
                  <Button variant="outline" size="sm" iconName="Smartphone">
                    View Backup Codes
                  </Button>
                  <Button variant="outline" size="sm" iconName="RotateCcw">
                    Regenerate Codes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showTwoFactorSetup && (
          <div className="border border-border rounded-lg p-6 mt-6">
            <h4 className="font-medium text-foreground mb-4">Set Up Two-Factor Authentication</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-medium text-foreground mb-3">Step 1: Install Authenticator App</h5>
                <p className="text-sm text-muted-foreground mb-4">
                  Download and install an authenticator app like Google Authenticator, Authy, or 1Password.
                </p>
                
                <h5 className="font-medium text-foreground mb-3">Step 2: Scan QR Code</h5>
                <div className="bg-muted rounded-lg p-6 text-center">
                  <div className="w-32 h-32 bg-white border-2 border-border rounded-lg mx-auto mb-3 flex items-center justify-center">
                    <Icon name="QrCode" size={64} className="text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Scan this QR code with your authenticator app
                  </p>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-foreground mb-3">Step 3: Enter Verification Code</h5>
                <p className="text-sm text-muted-foreground mb-4">
                  Enter the 6-digit code from your authenticator app to complete setup.
                </p>
                
                <Input
                  label="Verification Code"
                  type="text"
                  placeholder="123456"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e?.target?.value)}
                  maxLength={6}
                  className="mb-4"
                />

                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowTwoFactorSetup(false)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    loading={loading}
                    onClick={handleEnableTwoFactor}
                    disabled={verificationCode?.length !== 6}
                  >
                    Enable 2FA
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Active Sessions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Active Sessions</h3>
            <p className="text-sm text-muted-foreground">Manage devices that are currently signed in to your account</p>
          </div>
          <Button variant="outline" iconName="LogOut">
            Sign Out All Devices
          </Button>
        </div>

        <div className="space-y-4">
          {activeSessions?.map((session) => (
            <div key={session?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                    <Icon name={getDeviceIcon(session?.device)} size={20} />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-foreground">{session?.device}</h4>
                      {session?.isCurrent && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          Current Session
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {session?.browser} • {session?.location}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {session?.ipAddress} • Last active {session?.lastActive}
                    </p>
                  </div>
                </div>

                {!session?.isCurrent && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="LogOut"
                    onClick={() => handleRevokeSession(session?.id)}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Login History */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Login History</h3>
            <p className="text-sm text-muted-foreground">Recent sign-in activity for your account</p>
          </div>
          <Button variant="outline" iconName="Download">
            Export History
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Date & Time</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Device</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Location</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">IP Address</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {loginHistory?.map((entry) => (
                <tr key={entry?.id} className="border-b border-border">
                  <td className="py-3 px-4 text-sm text-foreground">
                    {new Date(entry.timestamp)?.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    <div>
                      <div>{entry?.device}</div>
                      <div className="text-xs text-muted-foreground">{entry?.browser}</div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">{entry?.location}</td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">{entry?.ipAddress}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      entry?.status === 'success' ?'bg-success/10 text-success' :'bg-destructive/10 text-destructive'
                    }`}>
                      <Icon 
                        name={entry?.status === 'success' ? 'Check' : 'X'} 
                        size={12} 
                        className="mr-1" 
                      />
                      {entry?.status === 'success' ? 'Success' : 'Blocked'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Security Recommendations */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Security Recommendations</h3>
          <p className="text-sm text-muted-foreground">Keep your account secure with these best practices</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Icon name="Shield" size={20} className="text-success mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground">Use Strong Passwords</h4>
                <p className="text-sm text-muted-foreground">
                  Use a unique, complex password with at least 12 characters
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Icon name="Smartphone" size={20} className="text-success mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground">Enable Two-Factor Authentication</h4>
                <p className="text-sm text-muted-foreground">
                  Add an extra layer of security with 2FA
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Icon name="Eye" size={20} className="text-success mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground">Monitor Account Activity</h4>
                <p className="text-sm text-muted-foreground">
                  Regularly review your login history and active sessions
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Icon name="Wifi" size={20} className="text-success mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground">Secure Network Connections</h4>
                <p className="text-sm text-muted-foreground">
                  Avoid using public Wi-Fi for sensitive account activities
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityTab;